import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CameraSlash, Camera } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const MODES = [
  { v: "first-of-month", l: "Primer turno del mes", d: "Solo el primer día que el colaborador firme en el mes." },
  { v: "every-day", l: "Todos los días", d: "Selfie obligatorio en cada firma." },
  { v: "weekdays", l: "Días laborables (elegir)", d: "Solo obligatorio en los días marcados abajo." },
  { v: "role", l: "Por rol / puesto", d: "Solo obligatorio para los puestos indicados." },
  { v: "disabled", l: "Deshabilitado", d: "Nunca se pide selfie (no recomendado)." },
];

const DOW_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export default function SelfieRulesPanel() {
  const [cfg, setCfg] = useState({ mode: "first-of-month", weekdays: [0, 1, 2, 3, 4], required_roles: [], hamming_threshold: 22 });
  const [newRole, setNewRole] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/selfie-rules").then((r) => setCfg({
      mode: r.data.mode || "first-of-month",
      weekdays: r.data.weekdays || [0, 1, 2, 3, 4],
      required_roles: r.data.required_roles || [],
      hamming_threshold: Number(r.data.hamming_threshold) || 22,
    })).catch(() => {});
  }, []);

  const toggleDay = (d) => {
    const w = cfg.weekdays.includes(d) ? cfg.weekdays.filter((x) => x !== d) : [...cfg.weekdays, d];
    setCfg({ ...cfg, weekdays: w.sort() });
  };
  const addRole = () => {
    const r = newRole.trim();
    if (!r) return;
    if (cfg.required_roles.includes(r)) return;
    setCfg({ ...cfg, required_roles: [...cfg.required_roles, r] });
    setNewRole("");
  };
  const removeRole = (r) => setCfg({ ...cfg, required_roles: cfg.required_roles.filter((x) => x !== r) });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/selfie-rules", cfg);
      toast.success("Reglas de selfie guardadas");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const active = MODES.find((m) => m.v === cfg.mode);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="selfie-rules-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-heading font-black text-slate-900 flex items-center gap-2">
            <Camera size={18} weight="bold" className="text-purple-700" /> Reglas de Selfie (asistencia)
          </h3>
          <p className="text-xs text-slate-500 mt-1">Configura cuándo el colaborador debe adjuntar selfie al firmar su asistencia. El selfie sirve para auditoría antisuplantación (rostro-match).</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Modo</Label>
        <div className="grid md:grid-cols-2 gap-2">
          {MODES.map((m) => (
            <button
              key={m.v}
              onClick={() => setCfg({ ...cfg, mode: m.v })}
              data-testid={`selfie-mode-${m.v}`}
              className={`text-left p-3 rounded border text-xs transition ${cfg.mode === m.v ? "bg-blue-900 text-white border-blue-900" : "bg-slate-50 border-slate-200 hover:border-blue-400"}`}
            >
              <p className="font-bold">{m.l}</p>
              <p className={`text-[10px] ${cfg.mode === m.v ? "text-blue-100" : "text-slate-500"}`}>{m.d}</p>
            </button>
          ))}
        </div>
      </div>

      {cfg.mode === "weekdays" && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Días con selfie obligatorio</Label>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {DOW_LABELS.map((lbl, i) => (
              <button
                key={i}
                onClick={() => toggleDay(i)}
                data-testid={`selfie-day-${i}`}
                className={`h-9 w-14 rounded text-xs font-bold border transition ${cfg.weekdays.includes(i) ? "bg-purple-600 text-white border-purple-700" : "bg-slate-50 text-slate-500 border-slate-200 hover:border-purple-300"}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      )}

      {cfg.mode === "role" && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Puestos / roles obligatorios (subcadena)</Label>
          <div className="flex gap-2 mt-2">
            <Input placeholder="Ej: perforista, supervisor, operador" value={newRole}
                   onChange={(e) => setNewRole(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && addRole()}
                   data-testid="selfie-role-input" />
            <Button variant="outline" onClick={addRole} data-testid="selfie-role-add">Añadir</Button>
          </div>
          <ul className="mt-3 space-y-1.5">
            {cfg.required_roles.length === 0 && <li className="text-xs text-slate-400 italic">Sin roles definidos — selfie no se pedirá a nadie.</li>}
            {cfg.required_roles.map((r) => (
              <li key={r} className="flex items-center justify-between text-xs bg-purple-50 border border-purple-200 rounded px-2 py-1.5">
                <span>{r}</span>
                <button onClick={() => removeRole(r)} className="text-red-500 hover:text-red-700 text-xs font-bold">×</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {cfg.mode === "disabled" && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-2 flex items-center gap-1.5">
          <CameraSlash size={14} weight="bold" /> El selfie nunca se pedirá. Perderás la protección antisuplantación.
        </p>
      )}

      <div className="pt-2 border-t border-slate-200">
        <div className="flex items-baseline justify-between mb-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">
            Umbral Rostro-Match (sensibilidad)
          </Label>
          <span className="text-xs font-mono font-bold text-purple-700">
            {cfg.hamming_threshold} / 64 bits
          </span>
        </div>
        <input
          type="range"
          min={12}
          max={30}
          step={1}
          value={cfg.hamming_threshold}
          onChange={(e) => setCfg({ ...cfg, hamming_threshold: Number(e.target.value) })}
          className="w-full accent-purple-600"
          data-testid="selfie-hamming-slider"
        />
        <div className="flex justify-between text-[9px] font-bold text-slate-500 mt-1">
          <span>12 · muy estricto (más flags)</span>
          <span>22 · balance</span>
          <span>30 · tolerante (menos flags)</span>
        </div>
        <p className="text-[10px] text-slate-500 italic mt-1">
          Ajusta si observas muchos falsos positivos (subir) o muchas suplantaciones sin detectar (bajar).
          Se aplica desde la próxima firma.
        </p>
      </div>

      <div className="pt-2">
        <p className="text-[11px] text-slate-500 italic mb-2">Configuración activa: <b>{active?.l}</b> · Umbral <b>{cfg.hamming_threshold}</b></p>
        <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="selfie-rules-save">
          {busy ? "Guardando…" : "Guardar reglas"}
        </Button>
      </div>
    </div>
  );
}
